// Locale registry + URL helpers — the single source of truth for the locale set.
//
// IMPORTANT: keep this file free of browser globals (window/document/navigator)
// so the Node build script (scripts/i18n-build.mjs) can import it directly.
//
// Model: the URL path PREFIX decides the copy locale. en-GB lives at the site
// root ('' prefix) and is the x-default; every other locale lives under a folder
// (e.g. /es, /pt-br). Region (north-america/brazil/europe — currency + logos) is
// a separate, orthogonal concern handled by scripts/Region.jsx.

import { USE_CASE_ROUTES } from '../use-case-routes.js';
export const DEFAULT_LOCALE = 'en-GB';

// Marketing's lead-capture flow must never use the App's /onboarding namespace.
export const SAMPLE_REPORT_START_ROUTE = 'sample-report/start';
export const SAMPLE_REPORT_SUCCESS_ROUTE = SAMPLE_REPORT_START_ROUTE + '/success';

// Full registry of every locale we have translations for. en-GB first because
// it is the root / x-default. es/de/fr stay here so their dictionaries keep
// resolving (and can be re-enabled later) even though they are not offered.
export const LOCALES = ['en-GB', 'en-US', 'es', 'de', 'fr', 'pt-BR'];

// Locales actually OFFERED to visitors right now: shown in the language picker,
// used as geo-redirect targets, advertised via hreflang/sitemap, and generated
// as static clones. We currently support only English and Brazilian Portuguese
// for live conversations, so es/de/fr are intentionally not surfaced — the
// translations are kept (see LOCALES) for a possible future revisit.
export const ENABLED_LOCALES = ['en-GB', 'en-US', 'pt-BR'];

// Served to Brazilian visitors by geo and directly reachable, but hidden from
// search discovery: no hreflang/sitemap/llms.txt entry, and every page is noindex.
// The header picker stays hidden. The /pt-br/* routes stay because the App's
// route-ownership contract reserves them; remove pt-BR from ENABLED_LOCALES
// only together with that contract (App first, then the marketing pin).
export const HIDDEN_LOCALES = ['pt-BR'];

// Locales advertised to search engines and visitors.
export const PUBLIC_LOCALES = ENABLED_LOCALES.filter((locale) => !HIDDEN_LOCALES.includes(locale));

// Locale -> URL folder prefix (no leading slash). '' == site root (en-GB).
export const LOCALE_TO_PREFIX = {
  'en-GB': '',
  'en-US': 'en-us',
  'es': 'es',
  'de': 'de',
  'fr': 'fr',
  'pt-BR': 'pt-br',
};

// URL folder prefix -> locale. Only the non-root locales appear here.
export const PREFIX_TO_LOCALE = {
  'en-us': 'en-US',
  'es': 'es',
  'de': 'de',
  'fr': 'fr',
  'pt-br': 'pt-BR',
};

// BCP-47 value used for <html lang> and hreflang. Bare language codes for
// es/de/fr deliberately target all speakers of that language, not one country.
export const LOCALE_HREFLANG = {
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  'es': 'es',
  'de': 'de',
  'fr': 'fr',
  'pt-BR': 'pt-BR',
};

// Default region (currency + integration-logo cluster) implied by each locale.
// The picker still lets the user mix region and language; this is only the
// sensible default applied when a user lands directly on a locale URL.
export const LOCALE_DEFAULT_REGION = {
  'en-GB': 'europe',
  'en-US': 'north-america',
  'es': 'europe',
  'de': 'europe',
  'fr': 'europe',
  'pt-BR': 'brazil',
};

// Pages that exist ONLY at the site root: English-only legal text. Links to
// these are never locale-prefixed, they are excluded from the
// per-locale build clones, and the edge middleware never geo-redirects them
// (their localized paths do not exist). Keyed by first URL segment.
export const NON_LOCALIZED_PATHS = new Set([
  'privacy', 'terms', 'security', 'subprocessors', 'dpa', 'docs',
]);

// Clean URL slugs that DO get a per-locale clone ('' === home). The build
// script, dev-server rewrite, and middleware all read this list. file name ===
// slug, except '' which maps to index.html.
export const LOCALIZED_PAGES = [
  '', 'product', 'about', 'contact', 'early-access', 'sample-report',
  SAMPLE_REPORT_START_ROUTE, 'integrations', 'use-cases', 'pricing', 'blog', 'blog-post',
  ...USE_CASE_ROUTES,
];

// Subset of LOCALIZED_PAGES listed in sitemap.xml. Excludes the conversion
// funnel and the dynamic blog-post template.
export const SITEMAP_PAGES = [
  '', 'product', 'about', 'contact',
  'integrations', 'use-cases', 'pricing', 'blog',
  ...USE_CASE_ROUTES,
];

// slug -> built HTML file name (without extension). The funnel has a nested
// directory index, so /sample-report/start is a real clean-URL artifact.
export function pageFile(slug) {
  if (slug === '') return 'index';
  if (slug === SAMPLE_REPORT_START_ROUTE) return 'sample-report/start/index';
  return slug;
}

// The funnel reuses its established onboarding title and description copy.
export function metadataKey(slug) {
  return slug === SAMPLE_REPORT_START_ROUTE ? 'onboarding' : pageFile(slug);
}

// Resolve the active locale from a pathname. Returns the matched prefix and a
// basePath ('' for root, '/es' for a folder locale) for link prefixing.
export function getLocaleFromPath(pathname) {
  const clean = String(pathname == null ? '/' : pathname).replace(/^\/+/, '');
  const seg = clean.split('/')[0].toLowerCase();
  const locale = PREFIX_TO_LOCALE[seg];
  if (locale) return { locale, prefix: seg, basePath: '/' + seg };
  return { locale: DEFAULT_LOCALE, prefix: '', basePath: '' };
}

// Strip any locale folder prefix, returning the canonical (root) path with a
// leading slash. '/es/product' -> '/product'; '/de' -> '/'; '/product' -> '/product'.
export function stripLocale(pathname) {
  const clean = String(pathname == null ? '/' : pathname).replace(/^\/+/, '');
  const parts = clean.split('/');
  if (PREFIX_TO_LOCALE[parts[0].toLowerCase()]) {
    const rest = parts.slice(1).join('/');
    return '/' + rest;
  }
  return '/' + clean;
}

// Prefix an internal app path with basePath, leaving non-internal links alone.
// External, protocol-relative, mailto/tel, hash-only and /api/* links pass
// through untouched. Safe to call at the site root (no-op) and idempotent.
export function applyBasePath(path, basePath) {
  if (!path) return path;
  const p = String(path);
  if (/^(?:[a-z]+:)?\/\//i.test(p)) return p;        // http(s):// or //host
  if (p.startsWith('mailto:') || p.startsWith('tel:')) return p;
  if (p.startsWith('#')) return p;                    // hash-only (same page)
  if (p.startsWith('/api/')) return p;                // API endpoints
  if (!p.startsWith('/')) return p;                   // relative / non-rooted
  if (!basePath) return p;                            // root locale -> no-op
  if (p === '/') return basePath;                     // home of the locale
  const prefix = basePath.replace(/^\//, '');
  // A query or fragment never names the segment: '/privacy#advertising-optout'
  // is the root-only privacy page, not a localizable 'privacy#…' slug.
  const firstSeg = p.replace(/^\//, '').split(/[/?#]/)[0].toLowerCase();
  if (firstSeg === prefix) return p;                  // already localized
  if (NON_LOCALIZED_PATHS.has(firstSeg)) return p;    // legal/utility — root only
  return basePath + p;
}

// Compute the path for the same page under a different locale (path only — the
// caller appends search/hash). '/es/product' + 'de' -> '/de/product'; a locale
// root gets a trailing slash ('/' + 'de' -> '/de/').
export function withLocale(pathname, targetLocale) {
  const base = LOCALE_TO_PREFIX[targetLocale] || '';
  const stripped = stripLocale(pathname);             // '/product' or '/'
  if (stripped === '/docs' || stripped.startsWith('/docs/')) return stripped;
  if (!base) return stripped;                         // root locale
  // Trailing slash: a locale root is a directory index and Cloudflare Pages
  // 308s '/en-us' to '/en-us/'. Point at the redirect target so the switcher
  // matches the canonical URL instead of costing a hop.
  if (stripped === '/' || stripped === '') return '/' + base + '/';
  return '/' + base + stripped;
}
